import {useLayoutEffect,useRef} from 'react';

let reflectionSequence=0;
// A decorative snapshot of the current DOM, never a second React tree: no data
// requests, subscriptions or action handlers. Scroll offsets are copied on the
// next animation frame. The layer is inert and absent from the accessible tree.
export default function SurfaceReflection(){
 const layerRef=useRef(null);
 useLayoutEffect(()=>{
  const layer=layerRef.current,source=layer.parentElement;
  let frame=0,dirty=true,pairs=[],disposed=false;
  const prefix=`reflection-${++reflectionSequence}-`;
  const own=node=>node instanceof Node&&(node===layer||layer.contains(node));
  const sync=()=>{
   frame=0;if(disposed||!source.isConnected)return;
   // Do not maintain hidden/mobile reflections.
   if(getComputedStyle(layer).display==='none'){layer.replaceChildren();pairs=[];dirty=true;return;}
   if(dirty){
    dirty=false;
    const copy=source.cloneNode(true);
    copy.querySelectorAll('[data-reflection-layer]').forEach(node=>node.remove());
    const originals=[source,...source.querySelectorAll('*')].filter(node=>!own(node));
    const copies=[copy,...copy.querySelectorAll('*')];
    const ids=new Map();copies.forEach(node=>{if(node.id)ids.set(node.id,prefix+node.id);});
    copies.forEach(node=>{
     node.removeAttribute('data-testid');node.removeAttribute('autofocus');
     node.removeAttribute('name');node.removeAttribute('aria-live');
     if(node.id)node.id=ids.get(node.id);
     for(const attr of [...node.attributes]){
      let value=attr.value;ids.forEach((next,old)=>{value=value.replaceAll(`url(#${old})`,`url(#${next})`);});
      if(value!==attr.value)node.setAttribute(attr.name,value);
     }
     if(node.matches('a,button,input,select,textarea,[tabindex]'))node.setAttribute('tabindex','-1');
     if(node.matches('a'))node.removeAttribute('href');
    });
    copy.classList.add('ref-reflection-copy');
    copy.style.setProperty('width',`${source.offsetWidth}px`,'important');
    copy.style.setProperty('height',`${source.offsetHeight}px`,'important');
    copy.style.setProperty('min-height','0','important');
    copy.style.setProperty('max-height','none','important');
    layer.replaceChildren(copy);
    pairs=originals.map((node,i)=>[node,copies[i]]);
    // Fix the visible scroll window, not the full length of its hidden data.
    pairs.forEach(([node,replica])=>{
     if(replica&&node.scrollHeight>node.clientHeight+1){replica.style.height=`${node.clientHeight}px`;replica.style.maxHeight=`${node.clientHeight}px`;replica.style.overflow='hidden';}
    });
   }
   pairs.forEach(([node,replica])=>{if(replica){replica.scrollTop=node.scrollTop;replica.scrollLeft=node.scrollLeft;if('value'in node)replica.value=node.value;}});
  };
  const schedule=(rebuild=false)=>{dirty=dirty||rebuild;if(!frame)frame=requestAnimationFrame(sync);};
  const observer=new MutationObserver(records=>{if(records.some(record=>!own(record.target)))schedule(true);});
  observer.observe(source,{subtree:true,childList:true,characterData:true,attributes:true});
  const resize=new ResizeObserver(()=>schedule(true));resize.observe(source);
  const scroll=event=>{if(!own(event.target))schedule();};
  const refresh=event=>{if(!event?.target||!own(event.target))schedule(true);};
  source.addEventListener('scroll',scroll,true);source.addEventListener('input',scroll,true);
  source.addEventListener('load',refresh,true);window.addEventListener('resize',refresh);
  schedule(true);
  return()=>{disposed=true;cancelAnimationFrame(frame);observer.disconnect();resize.disconnect();source.removeEventListener('scroll',scroll,true);source.removeEventListener('input',scroll,true);source.removeEventListener('load',refresh,true);window.removeEventListener('resize',refresh);layer.replaceChildren();};
 },[]);
 return <div ref={layerRef} className="ref-surface-reflection" data-reflection-layer="true" aria-hidden="true" inert={true}/>;
}
